import { Sparkles } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from 'components/Button'
import { Text } from 'components/Text'
import { cn } from 'lib/utils'
import { useAutopilotStore } from 'modules/autopilot/store/autopilot-store'

/**
 * Slim weekly-plan pitch shown above the always-on calendar grid when the
 * business has no active autopilot plan. Replaces the old AutopilotEducation
 * full-page takeover, which swapped out the entire grid and left fresh
 * accounts with no path to tap-a-day creation or the "Upload your own" door.
 *
 * Deliberately calm (bg-bg-2 + border-line); the acid CTA is the one loud
 * element. No dismiss control on purpose — the banner disappears on its own
 * once a plan exists. Renders nothing while the plan fetch is unresolved or
 * failed: on a failed fetch the empty list may be a lie (see the store's
 * `loadFailed` contract), and pitching "set up a plan" at someone who already
 * has one is worse than staying quiet for a session.
 */
export function PlanPitchBanner({ className }: { className?: string }) {
  const navigate = useNavigate()
  const autopilots = useAutopilotStore(s => s.autopilots)
  const loaded = useAutopilotStore(s => s.loaded)
  const loadFailed = useAutopilotStore(s => s.loadFailed)
  const fetchAutopilots = useAutopilotStore(s => s.fetchAutopilots)

  // Same lazy-fetch pattern as AutopilotWidget / WeeklyCockpit.
  useEffect(() => {
    if (!loaded) void fetchAutopilots()
  }, [loaded, fetchAutopilots])

  if (!loaded || loadFailed || autopilots.length > 0) return null

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-[12px] border border-line bg-bg-2 p-4 sm:flex-row sm:items-center sm:gap-4',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
        <div
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] border border-line-bright bg-bg-3 text-acid"
          aria-hidden
        >
          <Sparkles size={16} />
        </div>
        <div className="min-w-0">
          <Text as="p" variant="body" weight="semibold">
            Let Taka plan your week
          </Text>
          <Text as="p" variant="body-sm" className="mt-0.5">
            A post for every day that matters, drafted for your review.
          </Text>
        </div>
      </div>
      <Button
        variant="t3-primary"
        size="t3-sm"
        onClick={() => navigate('/?plan=open')}
        className="w-full sm:w-auto sm:flex-shrink-0"
      >
        Set up my plan
      </Button>
    </div>
  )
}
